import { useQuery } from '@pinia/colada'

const CHAT_ROLES: string[] = [
  AUTH0_ROLE_NAMES.engineeringWorkflows.id,
  AUTH0_ROLE_NAMES.product.id,
  AUTH0_ROLE_NAMES.engineeringCoreClaims.id,
  AUTH0_ROLE_NAMES.engineeringEngines.id,
]

/**
 * Whether the agent chat can work: the API answers and its AI gateway does. An
 * unreachable API, a failed check or a signed-out user all read as unavailable,
 * so the chat UI stays hidden rather than opening onto a connection error.
 * It is also limited to local dev and the teams in CHAT_ROLES.
 *
 * ponytail: a UI gate only; the API still serves any signed-in user. Enforce
 * the teams in the UserAgent worker if this must be a security boundary.
 */
export const useChatAvailable = createSharedComposable(() => {
  const { loggedIn, user } = useUserSession()
  const permitted = computed(
    () =>
      import.meta.dev ||
      (user.value?.['https://auth.rialtic.io/roles'] ?? []).some((role) =>
        CHAT_ROLES.includes(role),
      ),
  )
  const { data } = useQuery({
    key: ['user-agent', 'chat-status'],
    query: () =>
      useRequestFetch()<{ available: boolean }>('/api/user-agent/chat/status'),
    enabled: () => loggedIn.value && permitted.value,
    staleTime: 5 * 60_000,
    persist: false,
  })
  return computed(
    () =>
      loggedIn.value && permitted.value && data.value?.available === true,
  )
})
